// Click-to-edit primitive used across the detail panels for title,
// description, and any free-text field. Mirrors the Drillcut
// RockHeader.tsx toggle-state pattern: read mode renders the value
// (plus a faint pencil icon on hover); clicking flips into edit mode
// with an autoselected input. Enter / blur commits via onCommit; Esc
// reverts without a save.
//
// Two surfaces:
//   InlineEditableText      — single-line input
//   InlineEditableMultiline — textarea, autosize via rows hint

import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
} from "react";
import { Pencil } from "lucide-react";
import { DC } from "../sheets/createSlideOver/primitives";
import { RichTextEditor } from "./RichTextEditor";

interface InlineEditableTextProps {
  value: string;
  onCommit: (next: string) => void | Promise<void>;
  placeholder?: string;
  /** Optional max length passed straight to the input. */
  maxLength?: number;
  /** Whether the field is required — commits with empty strings
   *  revert instead of submitting. */
  required?: boolean;
  /** Style hooks for the read-mode container so callers can match
   *  the surrounding type scale (e.g. h1 title vs body paragraph). */
  readStyle?: CSSProperties;
  /** Custom display renderer for the read mode — defaults to the
   *  raw text. Use when the value is rich (e.g. HTML) and the
   *  read-mode view should differ from the edit-mode raw text. */
  renderRead?: (value: string) => React.ReactNode;
  /** Allows a plain text-mode editor for HTML descriptions. */
  isHtml?: boolean;
}

export function InlineEditableText({
  value,
  onCommit,
  placeholder = "Click to add…",
  maxLength,
  required,
  readStyle,
  renderRead,
}: InlineEditableTextProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [pending, setPending] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  // Mount → focus + select-all on the next tick so the autoselect
  // survives the layout flush. Mirrors RockHeader.tsx:161-164.
  useEffect(() => {
    if (!editing) return;
    const t = setTimeout(() => inputRef.current?.select(), 0);
    return () => clearTimeout(t);
  }, [editing]);

  async function commit() {
    const next = draft.trim();
    if (required && next.length === 0) {
      setEditing(false);
      setDraft(value);
      return;
    }
    if (next === value) {
      setEditing(false);
      return;
    }
    setPending(true);
    try {
      await onCommit(next);
      setEditing(false);
    } finally {
      setPending(false);
    }
  }

  function onKey(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter") {
      e.preventDefault();
      void commit();
    } else if (e.key === "Escape") {
      e.preventDefault();
      setEditing(false);
      setDraft(value);
    }
  }

  if (editing) {
    return (
      <input
        ref={inputRef}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => void commit()}
        onKeyDown={onKey}
        maxLength={maxLength}
        disabled={pending}
        style={{
          ...readStyle,
          width: "100%",
          padding: "2px 6px",
          margin: "-2px -6px",
          background: DC.surface,
          border: `1.5px solid ${DC.primary}`,
          borderRadius: 4,
          outline: 0,
          color: "var(--bsp-fg)",
        }}
      />
    );
  }

  const displayed = value && value.trim().length > 0
    ? renderRead
      ? renderRead(value)
      : value
    : (
      <span style={{ color: DC.grey, fontStyle: "italic" }}>{placeholder}</span>
    );

  return (
    <span
      onClick={() => setEditing(true)}
      style={{
        ...readStyle,
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        cursor: "text",
        position: "relative",
      }}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          setEditing(true);
        }
      }}
    >
      <span>{displayed}</span>
      <Pencil
        size={12}
        style={{
          opacity: 0,
          color: DC.grey,
          transition: "opacity 120ms",
        }}
        className="tp-inline-pencil"
      />
    </span>
  );
}

interface InlineEditableMultilineProps {
  value: string;
  onCommit: (next: string) => void | Promise<void>;
  placeholder?: string;
  /** Approximate rows the textarea opens with — autoresizes after. */
  rows?: number;
  /** When true, the value is HTML (from RichTextEditor) and the read
   *  mode renders via dangerouslySetInnerHTML rather than as a text
   *  block. Edit mode still shows a plain textarea — wholesale rich-
   *  text editing on the description is via the inline RichTextEditor
   *  in the create slide-over, not this primitive. */
  isHtml?: boolean;
}

export function InlineEditableMultiline({
  value,
  onCommit,
  placeholder = "Click to add a description…",
  rows = 3,
  isHtml,
}: InlineEditableMultilineProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [pending, setPending] = useState(false);
  const ref = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  useEffect(() => {
    if (!editing) return;
    const t = setTimeout(() => ref.current?.focus(), 0);
    return () => clearTimeout(t);
  }, [editing]);

  async function commit() {
    if (draft === value) {
      setEditing(false);
      return;
    }
    setPending(true);
    try {
      await onCommit(draft);
      setEditing(false);
    } finally {
      setPending(false);
    }
  }

  if (editing) {
    return (
      <textarea
        ref={ref}
        rows={rows}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => void commit()}
        onKeyDown={(e) => {
          // Esc reverts; Cmd/Ctrl+Enter commits. Plain Enter stays
          // a newline so the body of a paragraph can grow naturally.
          if (e.key === "Escape") {
            e.preventDefault();
            setEditing(false);
            setDraft(value);
          } else if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            void commit();
          }
        }}
        disabled={pending}
        placeholder={placeholder}
        style={{
          width: "100%",
          padding: "10px 12px",
          background: DC.surface,
          border: `1.5px solid ${DC.primary}`,
          borderRadius: 6,
          outline: 0,
          font: "400 13px/1.45 var(--bsp-font-sans)",
          color: "var(--bsp-fg)",
          resize: "vertical",
          minHeight: rows * 22 + 24,
        }}
      />
    );
  }

  const empty = !value || value === "<p></p>" || value.replace(/<[^>]*>/g, "").trim() === "";

  return (
    <div
      onClick={() => setEditing(true)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          setEditing(true);
        }
      }}
      style={{
        cursor: "text",
        padding: "8px 10px",
        margin: "-8px -10px",
        borderRadius: 6,
        transition: "background 120ms",
      }}
    >
      {empty ? (
        <span style={{ color: DC.grey, fontStyle: "italic", fontSize: 13 }}>
          {placeholder}
        </span>
      ) : isHtml ? (
        <div
          style={{ fontSize: 13, lineHeight: 1.5 }}
          dangerouslySetInnerHTML={{ __html: value }}
        />
      ) : (
        <div style={{ whiteSpace: "pre-wrap", fontSize: 13, lineHeight: 1.5 }}>
          {value}
        </div>
      )}
    </div>
  );
}

interface InlineEditableRichTextProps {
  value: string;
  onCommit: (next: string) => void | Promise<void>;
  placeholder?: string;
  /** Approximate rows the editor opens with — autoresizes after. */
  rows?: number;
}

/**
 * Click-to-edit wrapper around RichTextEditor. Read mode renders the
 * stored HTML via dangerouslySetInnerHTML; clicking flips into edit
 * mode with the full WYSIWYG toolbar (bold / italic / underline /
 * bulleted / numbered / quote / clear / link). Save commits; Cancel
 * reverts. The save / cancel button pair is explicit because plain
 * Enter inside the editor is a newline, not a submit, so blur-commit
 * would be ambiguous when the user clicks a toolbar button.
 *
 * Used by RockSheet description, TodoSheet description, IssueSheet
 * story — every "long-form" field on a planner detail panel.
 */
export function InlineEditableRichText({
  value,
  onCommit,
  placeholder = "Click to add a description…",
  rows = 4,
}: InlineEditableRichTextProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  async function commit() {
    if (draft === value) {
      setEditing(false);
      return;
    }
    setPending(true);
    try {
      await onCommit(draft);
      setEditing(false);
    } finally {
      setPending(false);
    }
  }

  if (editing) {
    return (
      <div>
        <RichTextEditor
          value={draft}
          onChange={setDraft}
          placeholder={placeholder}
          minRows={rows}
        />
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 6,
            marginTop: 8,
          }}
        >
          <button
            type="button"
            className="bsp-btn bsp-btn-secondary bsp-btn-sm"
            onClick={() => {
              setEditing(false);
              setDraft(value);
            }}
            disabled={pending}
          >
            Cancel
          </button>
          <button
            type="button"
            className="bsp-btn bsp-btn-primary bsp-btn-sm"
            onClick={() => void commit()}
            disabled={pending}
          >
            {pending ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    );
  }

  const empty =
    !value || value === "<p></p>" || value.replace(/<[^>]*>/g, "").trim() === "";

  return (
    <div
      onClick={() => setEditing(true)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          setEditing(true);
        }
      }}
      style={{
        cursor: "text",
        padding: "8px 10px",
        margin: "-8px -10px",
        borderRadius: 6,
        transition: "background 120ms",
      }}
    >
      {empty ? (
        <span style={{ color: DC.grey, fontStyle: "italic", fontSize: 13 }}>
          {placeholder}
        </span>
      ) : (
        <div
          style={{ fontSize: 13, lineHeight: 1.5 }}
          dangerouslySetInnerHTML={{ __html: value }}
        />
      )}
    </div>
  );
}
